import omitDeep from "omit-deep-lodash";
import * as yup from "yup";
import {
  ChamberInput,
  GetChamberQuery,
  UpdateChamberMutation,
} from "../../../../graphql/types";
import { WEBSITE_REGEX } from "../../../../utils/validationPatterns";
import {
  EMAIL_FORMAT_MESSAGE,
  enterFieldMessage,
  selectFieldMessage,
  WEB_ADDRESS_FORMAT_MESSAGE,
} from "../../../../utils/fieldMessages";
import {
  CMP_DEFAULT_ORGANIZATION_NOUN,
  CmpOrganizationNoun,
} from "../../../../utils/cmpOrganizationNoun";

export const CHAMBER_DESCRIPTION_CHARACTER_LIMIT = 2000;

export type ChamberProfileFormValues = ChamberInput;

type ChamberProfileSource =
  | NonNullable<GetChamberQuery["getChamber"]>
  | NonNullable<UpdateChamberMutation["updateChamber"]>;

const optionalWebsite = yup
  .string()
  .test(
    "optional-website",
    WEB_ADDRESS_FORMAT_MESSAGE,
    (value) => !value?.trim() || WEBSITE_REGEX.test(value.trim()),
  );

const normalizeBanners = (
  banners:
    | ChamberProfileSource["profileBanner"]
    | ChamberProfileSource["directoryBanner"],
) =>
  banners
    ? [...banners]
        .sort((a, b) => a.position - b.position)
        .map((banner, index) => ({ ...banner, position: index + 1 }))
    : undefined;

export const createChamberProfileValidationSchema = (
  noun: CmpOrganizationNoun = CMP_DEFAULT_ORGANIZATION_NOUN,
) => yup.object({
  name: yup.string().required(enterFieldMessage(`${noun.Singular} Name`)),
  description: yup
    .string()
    .max(
      CHAMBER_DESCRIPTION_CHARACTER_LIMIT,
      `Shorten the ${noun.Singular} Description to ${CHAMBER_DESCRIPTION_CHARACTER_LIMIT.toLocaleString("en-CA")} characters or fewer.`,
    ),
  website: yup
    .string()
    .matches(WEBSITE_REGEX, WEB_ADDRESS_FORMAT_MESSAGE)
    .required(enterFieldMessage(`${noun.Singular} Website`)),
  supportContactName: yup
    .string()
    .required(enterFieldMessage("Chamber Perks App™ Administrator Contact")),
  supportContactEmail: yup
    .string()
    .email(EMAIL_FORMAT_MESSAGE)
    .required(enterFieldMessage("Chamber Perks App™ Administrator Email")),
  billingContactName: yup.string().required(enterFieldMessage("Billing Contact Name")),
  billingContactEmail: yup
    .string()
    .email(EMAIL_FORMAT_MESSAGE)
    .required(enterFieldMessage("Billing Contact Email")),
  signingAuthorityContactName: yup
    .string()
    .required(enterFieldMessage("Signing Authority Contact Name")),
  signingAuthorityContactEmail: yup
    .string()
    .email(EMAIL_FORMAT_MESSAGE)
    .required(enterFieldMessage("Signing Authority Contact Email")),
  secondarySupportEmail: yup.string().email(EMAIL_FORMAT_MESSAGE),
  chamberSupportPageUrl: optionalWebsite,
  address: yup.object({
    streetAddress: yup.string().required(enterFieldMessage("Street Address or PO Box")),
    city: yup.string().required(enterFieldMessage("City")),
    province: yup.string().required(selectFieldMessage("Province/State")),
    postalCode: yup.string().required(enterFieldMessage("Postal/Zip Code")),
  }),
  supportLink: optionalWebsite,
  jobPostingLink: optionalWebsite,
  eventsLink: optionalWebsite,
  newsletterLink: optionalWebsite,
  surveyLink: optionalWebsite,
  advocacyWinsLink: optionalWebsite,
  callForNominationsLink: optionalWebsite,
  memberSignupLink: optionalWebsite,
  sponsorLogoLink: optionalWebsite,
  facebook: optionalWebsite,
  instagram: optionalWebsite,
  threads: optionalWebsite,
  linkedin: optionalWebsite,
  twitter: optionalWebsite,
  tiktok: optionalWebsite,
  youtube: optionalWebsite,
});

export const createChamberProfileInitialValues = ({
  chamber,
}: {
  chamber?: ChamberProfileSource | null;
}): ChamberProfileFormValues => {
  const initialValues: ChamberProfileFormValues = {
    id: chamber?.id || "",
    name: chamber?.name || "",
    description: chamber?.description || "",
    website: chamber?.website || "",
    supportContactEmail: chamber?.supportContactEmail || "",
    supportContactName: chamber?.supportContactName || "",
    billingContactName: chamber?.billingContactName || "",
    billingContactEmail: chamber?.billingContactEmail || "",
    signingAuthorityContactName: chamber?.signingAuthorityContactName || "",
    signingAuthorityContactEmail: chamber?.signingAuthorityContactEmail || "",
    supportLink: chamber?.supportLink || "",
    secondarySupportEmail: chamber?.secondarySupportEmail || "",
    chamberSupportPageUrl: chamber?.chamberSupportPageUrl || "",
    phoneNumber: chamber?.phoneNumber || "",
    phoneNumberExtension: chamber?.phoneNumberExtension || "",
    facebook: chamber?.facebook || "",
    instagram: chamber?.instagram || "",
    threads: chamber?.threads || "",
    linkedin: chamber?.linkedin || "",
    twitter: chamber?.twitter || "",
    tiktok: chamber?.tiktok || "",
    youtube: chamber?.youtube || "",
    jobPostingLink: chamber?.jobPostingLink || "",
    eventsLink: chamber?.eventsLink || "",
    newsletterLink: chamber?.newsletterLink || "",
    surveyLink: chamber?.surveyLink || "",
    advocacyWinsLink: chamber?.advocacyWinsLink || "",
    callForNominationsLink: chamber?.callForNominationsLink || "",
    memberSignupLink: chamber?.memberSignupLink || "",
    address: {
      city: chamber?.address.city || "",
      postalCode: chamber?.address.postalCode || "",
      province: chamber?.address.province || "",
      streetAddress: chamber?.address.streetAddress || "",
    },
    logo: chamber?.logo || undefined,
    sponsorLogo: chamber?.sponsorLogo || undefined,
    sponsorLogoLink: chamber?.sponsorLogoLink || "",
    directoryBanner: normalizeBanners(chamber?.directoryBanner),
    profileBanner: normalizeBanners(chamber?.profileBanner),
  };

  return omitDeep(initialValues, "__typename") as ChamberProfileFormValues;
};

const normalizeEmail = (value?: string | null) =>
  (value || "").trim().toLowerCase();

export const createChamberProfileUpdateInput = ({
  values,
}: {
  values: ChamberProfileFormValues;
}): ChamberInput => ({
  id: values.id,
  name: values.name,
  description: values.description,
  website: values.website,
  supportContactName: values.supportContactName,
  supportContactEmail: normalizeEmail(values.supportContactEmail),
  billingContactName: values.billingContactName,
  billingContactEmail: normalizeEmail(values.billingContactEmail),
  signingAuthorityContactName: values.signingAuthorityContactName,
  signingAuthorityContactEmail: normalizeEmail(
    values.signingAuthorityContactEmail,
  ),
  phoneNumber: values.phoneNumber,
  phoneNumberExtension: values.phoneNumberExtension,
  supportLink: values.supportLink,
  secondarySupportEmail: normalizeEmail(values.secondarySupportEmail),
  chamberSupportPageUrl: values.chamberSupportPageUrl,
  address: {
    streetAddress: values.address.streetAddress,
    city: values.address.city,
    province: values.address.province,
    postalCode: values.address.postalCode,
  },
  facebook: values.facebook,
  instagram: values.instagram,
  threads: values.threads,
  linkedin: values.linkedin,
  twitter: values.twitter,
  tiktok: values.tiktok,
  youtube: values.youtube,
  eventsLink: values.eventsLink,
  newsletterLink: values.newsletterLink,
  jobPostingLink: values.jobPostingLink,
  surveyLink: values.surveyLink,
  advocacyWinsLink: values.advocacyWinsLink,
  memberSignupLink: values.memberSignupLink,
  callForNominationsLink: values.callForNominationsLink,
  logo: values.logo,
  sponsorLogo: values.sponsorLogo,
  sponsorLogoLink: values.sponsorLogoLink,
  profileBanner: values.profileBanner,
  directoryBanner: values.directoryBanner,
});
